import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Input, Select } from '@shared/ui/Input';

export const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding-bottom: ${({ theme }) => theme.spacing(1)};
`;

export const Tab = styled.button<{ $active: boolean }>`
  border: none;
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  white-space: nowrap;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const TabHint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 760px;
`;

export const FilterBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

export const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-width: 200px;
  flex: 1;
`;

export const SectionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.surface};
    white-space: nowrap;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

export const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => `${theme.spacing(4)} 0`};
`;

export const ErrBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.danger}12;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

export const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  flex-wrap: wrap;
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.status.danger};
    border-color: ${({ theme }) => theme.colors.status.danger}66;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const ExpandButton = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(1.5)}`};
  margin-left: ${({ theme }) => `-${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.primarySoft};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  svg {
    flex-shrink: 0;
    transition: transform ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  }
`;

export const LotName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: end;
`;

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-width: 0;
`;

export const FormActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const InlineHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Скрытый нативный file-input + видимая кнопка (как в MtoRunsList). */
export const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

export const FilePick = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  min-width: 0;
  padding: ${({ theme }) => `0 ${theme.spacing(3.5)}`};
  height: 40px;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const DocFormCard = styled(Card)`
  background: ${({ theme }) => theme.colors.background};
  box-shadow: none;
`;

export const DocsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(2)} 0;
`;

/** Ячейка раскрытой партии под документы — мягкий фон отделяет вложенный блок
 *  от строк реестра (как IndicatorCell/CandidateCell в MtoRunDetail). */
export const DocsCell = styled.td`
  background: ${({ theme }) => theme.colors.background};
`;

export const AddLotCard = styled(Card)`
  background: ${({ theme }) => theme.colors.background};
  box-shadow: none;
`;

export const AddAppCard = styled(Card)`
  background: ${({ theme }) => theme.colors.background};
  box-shadow: none;
`;

export const DeterministicNote = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.muted};
  max-width: 760px;
`;

export const CellSelect = styled(Select)`
  height: 34px;
  min-width: 148px;
  font-size: 12.5px;
`;

export const CellInput = styled(Input)`
  height: 34px;
  min-width: 120px;
  font-size: 12.5px;
`;

export const NewLotFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

export const FileCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
  min-width: 180px;
  max-width: 260px;
`;

export const FileNameText = styled.span`
  font-weight: 600;
  font-size: 12.5px;
  word-break: break-word;
`;

export const RowNote = styled.span`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.status.warning};
  line-height: 1.4;
`;

/** «Документ 3 из 31 · стр. 5–6»: пачка паспортов в одном PDF разделена. */
export const PartNote = styled.span`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
`;

export const ImportBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

export const SummaryLine = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;
